import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatPrice, logger } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { FadeIn } from "../components/FadeIn";

const AccountHeader = ({ user, onLogout }) => (
  <div className="flex items-center justify-between flex-wrap gap-4 mb-12">
    <div className="flex items-center gap-4">
      {user.picture ? (
        <img src={user.picture} alt="" className="h-14 w-14 rounded-full object-cover border border-capra-bronze/50" />
      ) : (
        <div className="h-14 w-14 rounded-full bg-capra-elevated border border-capra-bronze/40 flex items-center justify-center font-display text-xl text-capra-bronze">
          {user.name?.[0]?.toUpperCase()}
        </div>
      )}
      <div>
        <h1 className="font-display text-3xl text-capra-ivory" data-testid="account-user-name">{user.name}</h1>
        <p className="text-xs text-capra-muted mt-1">{user.email} · {user.provider === "google" ? "Google" : "Email"} account</p>
      </div>
    </div>
    <button
      data-testid="logout-button"
      onClick={onLogout}
      className="px-6 py-3 border border-white/15 text-capra-muted text-[11px] uppercase tracking-[0.22em] hover:border-capra-copper hover:text-capra-copper transition-colors"
    >
      Sign Out
    </button>
  </div>
);

const OrdersList = ({ orders }) => {
  if (orders.length === 0) {
    return (
      <div className="leather-panel p-10 text-center" data-testid="no-orders">
        <p className="font-display text-xl text-capra-ivory mb-3">No orders yet.</p>
        <p className="text-sm text-capra-muted mb-6">Your first piece of the maison awaits.</p>
        <Link
          to="/new-collection"
          className="inline-block px-8 py-3.5 bg-capra-bronze text-capra-night text-[11px] uppercase tracking-[0.22em] font-semibold hover:bg-capra-amber transition-colors"
        >
          Explore Collection
        </Link>
      </div>
    );
  }
  return (
    <div className="space-y-4" data-testid="orders-list">
      {orders.map((o) => (
        <div key={o.order_id} className="leather-panel p-6 flex flex-wrap items-center justify-between gap-4" data-testid={`order-${o.order_id}`}>
          <div>
            <p className="text-sm text-capra-ivory font-medium">{o.order_id}</p>
            <p className="text-xs text-capra-muted mt-1">
              {new Date(o.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}
            </p>
            <p className="text-xs text-capra-muted mt-2">
              {o.items?.map((it) => `${it.name} × ${it.qty}`).join(" · ")}
            </p>
          </div>
          <p className="font-display text-xl text-capra-gold">{formatPrice(o.amount)}</p>
        </div>
      ))}
    </div>
  );
};

export default function Account() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [loading, user, navigate]);

  useEffect(() => {
    if (user) {
      api.get("/orders/mine")
        .then(({ data }) => setOrders(data))
        .catch((err) => logger.error("Orders fetch failed", err));
    }
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  if (loading || !user)
    return (
      <div className="pt-44 pb-32 text-center text-capra-muted font-display italic" data-testid="account-loading">
        Entering the maison…
      </div>
    );

  return (
    <div className="pt-36 lg:pt-44 pb-24 px-6 lg:px-10 max-w-[1000px] mx-auto" data-testid="account-page">
      <FadeIn>
        <AccountHeader user={user} onLogout={handleLogout} />
        <h2 className="overline-label mb-6">Order History</h2>
        <OrdersList orders={orders} />
      </FadeIn>
    </div>
  );
}
